import { z } from 'zod';
import { ClubHostPublicSchema } from './club-hosts.js';
import { ClubStatusSchema } from './clubs.js';

export const ClubAccessStatusSchema = z.enum(['owned', 'locked']);
export type ClubAccessStatus = z.infer<typeof ClubAccessStatusSchema>;

export const ChapterAccessSchema = z.enum(['demo', 'owned', 'locked']);
export type ChapterAccess = z.infer<typeof ChapterAccessSchema>;

export const ChapterProgressStateSchema = z.enum(['not_started', 'in_progress', 'completed']);
export type ChapterProgressState = z.infer<typeof ChapterProgressStateSchema>;

export const CatalogQuerySchema = z.object({
  q: z.string().max(200).optional(),
  status: ClubStatusSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type CatalogQuery = z.infer<typeof CatalogQuerySchema>;

export const CatalogClubItemSchema = z.object({
  id: z.string(),
  paywallSlug: z.string().nullable(),
  title: z.string(),
  status: ClubStatusSchema,
  isUnpublished: z.boolean(),
  bookAuthor: z.string().nullable(),
  host: ClubHostPublicSchema,
  secondHost: ClubHostPublicSchema.nullable(),
  author: z.string().nullable(),
  authorSubtitle: z.string().nullable(),
  authorAvatarUrl: z.string().nullable(),
  secondAuthor: z.string().nullable(),
  secondAuthorSubtitle: z.string().nullable(),
  secondAuthorAvatarUrl: z.string().nullable(),
  coverUrl: z.string().nullable(),
  accessStatus: ClubAccessStatusSchema,
});
export type CatalogClubItem = z.infer<typeof CatalogClubItemSchema>;

export const CatalogResponseSchema = z.object({
  items: z.array(CatalogClubItemSchema),
  nextCursor: z.string().optional(),
});
export type CatalogResponse = z.infer<typeof CatalogResponseSchema>;

export const ClubPageChapterSchema = z.object({
  id: z.string(),
  index: z.number(),
  title: z.string(),
  subtitle: z.string().nullable(),
  isDemo: z.boolean(),
  isUnpublished: z.boolean(),
  access: ChapterAccessSchema,
  progressState: ChapterProgressStateSchema,
});
export type ClubPageChapter = z.infer<typeof ClubPageChapterSchema>;

export const ClubPurchaseStateSchema = z.object({
  canPurchase: z.boolean(),
});
export type ClubPurchaseState = z.infer<typeof ClubPurchaseStateSchema>;

export const ClubPageContentSummarySchema = z.object({
  videoMessageCount: z.number().int().min(0),
});
export type ClubPageContentSummary = z.infer<typeof ClubPageContentSummarySchema>;

export const PaywallOfferSchema = z.object({
  club: z.object({
    id: z.string(),
    paywallSlug: z.string().nullable(),
    title: z.string(),
    status: ClubStatusSchema,
    bookAuthor: z.string().nullable(),
    host: ClubHostPublicSchema,
    secondHost: ClubHostPublicSchema.nullable(),
    author: z.string().nullable(),
    authorSubtitle: z.string().nullable(),
    authorAvatarUrl: z.string().nullable(),
    coverUrl: z.string().nullable(),
    shortDescription: z.string().nullable(),
    price: z.object({ amount: z.number(), currency: z.string() }).nullable(),
  }),
  purchase: ClubPurchaseStateSchema,
  accessStatus: ClubAccessStatusSchema,
  hasDemo: z.boolean(),
});
export type PaywallOffer = z.infer<typeof PaywallOfferSchema>;

export const ClubPageResponseSchema = z.object({
  club: z.object({
    id: z.string(),
    paywallSlug: z.string().nullable(),
    title: z.string(),
    status: ClubStatusSchema,
    bookAuthor: z.string().nullable(),
    host: ClubHostPublicSchema,
    secondHost: ClubHostPublicSchema.nullable(),
    author: z.string().nullable(),
    authorSubtitle: z.string().nullable(),
    authorAvatarUrl: z.string().nullable(),
    secondAuthor: z.string().nullable(),
    secondAuthorSubtitle: z.string().nullable(),
    secondAuthorAvatarUrl: z.string().nullable(),
    coverUrl: z.string().nullable(),
    shortDescription: z.string().nullable(),
    price: z.object({ amount: z.number(), currency: z.string() }).nullable(),
  }),
  purchase: ClubPurchaseStateSchema,
  accessStatus: ClubAccessStatusSchema,
  progress: z
    .object({
      percentComplete: z.number(),
      updatedAt: z.string(),
    })
    .nullable(),
  continueItem: z
    .object({
      chapterId: z.string(),
      chapterTitle: z.string(),
      chapterIndex: z.number(),
      lastPosition: z.number().nullable(),
      updatedAt: z.string().nullable(),
    })
    .nullable(),
  contentSummary: ClubPageContentSummarySchema,
  chapters: z.array(ClubPageChapterSchema),
});
export type ClubPageResponse = z.infer<typeof ClubPageResponseSchema>;
